import { useEffect, useRef, useCallback } from 'react';
import config from '../../../config';
import { acquireEditLock, heartbeatEditLock, releaseEditLock } from '../../../utils/canvasApi';

const HEARTBEAT_INTERVAL_MS = 10_000;
const RETRY_INTERVAL_MS = 5_000;

/**
 * Manages the pessimistic editing lock lifecycle for a workflow canvas.
 *
 * - Acquires lock on mount (call `tryAcquire` after canvas loads)
 * - Sends heartbeat every 10s to keep lock alive (server TTL is 30s)
 * - If locked by another session, polls every 5s to auto-acquire when freed
 * - Releases lock on unmount and beforeunload via navigator.sendBeacon
 */
export default function useEditLock(projectId, { enabled = true, onLocked, onLockAcquired, onLockLost } = {}) {
  const lockTokenRef = useRef(null);
  const canonicalProjectIdRef = useRef(null);
  const heartbeatRef = useRef(null);
  const retryRef = useRef(null);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const onLockedRef = useRef(onLocked);
  onLockedRef.current = onLocked;
  const onLockAcquiredRef = useRef(onLockAcquired);
  onLockAcquiredRef.current = onLockAcquired;
  const onLockLostRef = useRef(onLockLost);
  onLockLostRef.current = onLockLost;

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const stopRetry = useCallback(() => {
    if (retryRef.current) {
      clearInterval(retryRef.current);
      retryRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    stopRetry();
    heartbeatRef.current = setInterval(async () => {
      try {
        const res = await heartbeatEditLock(projectIdRef.current);
        const status = res?.status || res?.response?.status;
        // Only declare lock lost on definitive server statuses (410 gone, 403 forbidden).
        // Missing status (network/timeout) or 5xx are transient — server TTL is 30s and we
        // heartbeat every 10s, so keep trying. Next definitive response will trip the loss.
        if (status === 410 || status === 403) {
          stopHeartbeat();
          lockTokenRef.current = null;
          onLockLostRef.current?.();
        }
      } catch (e) {
        // send_request resolves errors, so this path is defensive. Treat as transient.
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [stopHeartbeat, stopRetry]);

  const startRetryPolling = useCallback(() => {
    stopRetry();
    stopHeartbeat();
    retryRef.current = setInterval(async () => {
      try {
        const res = await acquireEditLock(projectIdRef.current);
        const lock = res?.data;
        if (!lock) return;

        if (lock.canonicalProjectId) {
          canonicalProjectIdRef.current = lock.canonicalProjectId;
        }

        if (lock.lockedBySelf) {
          stopRetry();
          lockTokenRef.current = lock.lockToken;
          startHeartbeat();
          onLockAcquiredRef.current?.();
        }
      } catch {
        // Network error — keep polling
      }
    }, RETRY_INTERVAL_MS);
  }, [stopRetry, stopHeartbeat, startHeartbeat]);

  const releaseViaBeacon = useCallback(() => {
    const token = lockTokenRef.current;
    const pid = canonicalProjectIdRef.current || projectIdRef.current;
    if (!token || !pid) return;

    const url = `${config.API_ADDRESS}/project-service/project/${pid}/canvas/lock/release`;
    const payload = new URLSearchParams({ lockToken: token });
    navigator.sendBeacon(url, payload);
    lockTokenRef.current = null;
  }, []);

  const tryAcquire = useCallback(async () => {
    if (!enabled || !projectId) return;

    try {
      const res = await acquireEditLock(projectId);
      const lock = res?.data;
      if (!lock) return;

      if (lock.canonicalProjectId) {
        canonicalProjectIdRef.current = lock.canonicalProjectId;
      }

      if (lock.lockedBySelf) {
        lockTokenRef.current = lock.lockToken;
        startHeartbeat();
        onLockAcquiredRef.current?.();
      } else if (lock.locked) {
        onLockedRef.current?.(lock.lockedBy);
        startRetryPolling();
      }
    } catch (e) {
      // Lock acquire failed — continue without lock
    }
  }, [enabled, projectId, startHeartbeat, startRetryPolling]);

  const release = useCallback(async () => {
    stopHeartbeat();
    stopRetry();
    if (!lockTokenRef.current || !projectIdRef.current) return;

    try {
      await releaseEditLock(projectIdRef.current);
    } catch {
      // Best-effort
    }
    lockTokenRef.current = null;
  }, [stopHeartbeat, stopRetry]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      releaseViaBeacon();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      stopHeartbeat();
      stopRetry();
      releaseViaBeacon();
    };
  }, [releaseViaBeacon, stopHeartbeat, stopRetry]);

  return { tryAcquire, release };
}
